
import React, { useState, useEffect } from 'react';
import { X, Palette, Settings, Save, RotateCcw } from 'lucide-react';
import { useThemeController, type ThemePreset } from '@/hooks/useThemeController';
import { useSettingsPanel } from '@/hooks/useSettingsPanel';
import { configService } from '@/services/ConfigService';
import SettingsToggle from '@/components/SettingsToggle';
import { DynamicSettingsTab } from './components';
import { cn } from '@/lib/utils';
import './EnhancedSettingsPanel.css';

interface EnhancedSettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const EnhancedSettingsPanel: React.FC<EnhancedSettingsPanelProps> = ({ isOpen, onClose }) => {
  const { 
    theme, 
    preset, 
    resolvedTheme,
    mounted,
    setTheme,
    setPreset,
    availableThemes,
    availablePresets
  } = useThemeController();

  const { getVisibleTabs, getTabLabels, getSettingValue } = useSettingsPanel();
  const [activeTab, setActiveTab] = useState('appearance');
  const [, setRefresh] = useState(0);

  // Listen for settings updates to trigger re-renders
  useEffect(() => {
    const handleSettingsUpdate = () => {
      setRefresh(prev => prev + 1);
    };

    window.addEventListener('settings-updated', handleSettingsUpdate);
    return () => window.removeEventListener('settings-updated', handleSettingsUpdate);
  }, []);

  if (!mounted || !isOpen) return null;

  const themeIcons = {
    light: Settings,
    dark: Settings,
    system: Settings
  };

  // Enhanced preset colors with three-color gradients
  const presetColors = {
    default: 'from-[#113E67] via-[#5C90BA] to-[#FFC242]',
    ocean: 'from-[#003851] via-[#279AB3] to-[#D89E45]',
    sunrise: 'from-[#AD3203] via-[#FF7C50] to-[#3DB3C8]',
    forest: 'from-[#132F13] via-[#4E8C4E] to-[#A1782C]',
    purple: 'from-[#330044] via-[#9656B3] to-[#F1A94D]',
    neutral: 'from-[#525779] via-[#A0A3C1] to-[#C29A4D]'
  };

  // Get dynamic tabs and add appearance tab LAST
  const dynamicTabs = getTabLabels();
  const allTabs = [
    ...dynamicTabs.map(tab => ({ 
      id: tab.name, 
      label: tab.label, 
      icon: Settings 
    })),
    { id: 'appearance', label: 'Appearance', icon: Palette }
  ];

  const handleReset = () => {
    setTheme('light');
    setPreset('default');
    
    // Reset dynamic settings
    configService.clearSettingsFromStorage();
    window.dispatchEvent(new CustomEvent('settings-updated'));
  };

  const handleDynamicSettingChange = (key: string, value: boolean) => {
    const currentSettings = configService.checkAndMapStorageData();
    const updatedSettings = { ...currentSettings, [key]: value };
    
    try {
      sessionStorage.setItem('config-settings-value-map', JSON.stringify(updatedSettings));
      window.dispatchEvent(new CustomEvent('settings-updated'));
    } catch (error) {
      console.error('Failed to save setting:', error);
    }
  };

  return (
    <div className="settings-overlay" onClick={onClose}>
      <div className="enhanced-settings-panel glass-surface" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="settings-header">
          <div className="header-content">
            <div className="header-icon">
              <Palette className="w-6 h-6" />
            </div>
            <div>
              <h2 className="settings-title">Settings</h2>
              <p className="settings-subtitle">Customize your application experience</p>
            </div>
          </div>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="settings-tabs">
          {allTabs.map((tab) => (
            <button
              key={tab.id}
              className={cn('tab-btn', activeTab === tab.id && 'active')}
              onClick={() => setActiveTab(tab.id)}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="settings-content">
          {/* Dynamic Configuration Tabs */}
          {dynamicTabs.map((tab) => (
            activeTab === tab.name && (
              <DynamicSettingsTab 
                key={tab.name} 
                tabName={tab.name} 
              />
            )
          ))}

          {/* Appearance Tab - Always Last */}
          {activeTab === 'appearance' && (
            <div className="appearance-settings">
              {/* Theme Mode */}
              <div className="setting-section">
                <h3 className="section-title">Theme Mode</h3>
                <div className="theme-mode-grid">
                  {availableThemes.map((themeOption) => {
                    const Icon = themeIcons[themeOption.value];
                    return (
                      <button
                        key={themeOption.value}
                        className={cn(
                          'theme-mode-btn',
                          theme === themeOption.value && 'active'
                        )}
                        onClick={() => setTheme(themeOption.value)}
                      >
                        <Icon className="w-5 h-5" />
                        <span>{themeOption.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Enhanced Color Presets */}
              <div className="setting-section">
                <h3 className="section-title">Color Presets</h3>
                <p className="section-description">Each preset includes primary, secondary (tint), and tertiary (complementary) colors</p>
                <div className="preset-grid">
                  {availablePresets.map((presetOption) => (
                    <button
                      key={presetOption.value}
                      className={cn(
                        'preset-btn enhanced',
                        preset === presetOption.value && 'active'
                      )}
                      onClick={() => setPreset(presetOption.value)}
                    >
                      <div className={cn(
                        'preset-color-indicator enhanced',
                        'bg-gradient-to-r',
                        presetColors[presetOption.value as keyof typeof presetColors]
                      )}>
                        <div className="color-dots">
                          <span className="primary-dot"></span>
                          <span className="secondary-dot"></span>
                          <span className="tertiary-dot"></span>
                        </div>
                      </div>
                      <div className="preset-info">
                        <span className="preset-name">{presetOption.label}</span>
                        <span className="preset-description">{presetOption.description}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Enhanced Preview */}
              <div className="setting-section">
                <h3 className="section-title">Live Preview</h3>
                <div className="theme-preview enhanced glass-surface">
                  <div className="preview-header">
                    <div className="preview-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                    <span className="preview-title">
                      {availablePresets.find(p => p.value === preset)?.label} Theme
                    </span>
                  </div>
                  <div className="preview-content enhanced">
                    <div className="preview-elements">
                      <div className="preview-card primary">
                        <div className="card-header">Primary</div>
                        <div className="card-content">Main actions & branding</div>
                      </div>
                      <div className="preview-card secondary">
                        <div className="card-header">Secondary</div>
                        <div className="card-content">Supporting elements</div>
                      </div>
                      <div className="preview-card tertiary">
                        <div className="card-header">Tertiary</div>
                        <div className="card-content">Accents & highlights</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="settings-footer">
          <button className="reset-btn" onClick={handleReset}>
            <RotateCcw className="w-4 h-4" />
            <span>Reset to Default</span>
          </button>
          <button className="save-btn" onClick={onClose}>
            <Save className="w-4 h-4" />
            <span>Save Changes</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnhancedSettingsPanel;
